import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    booleanAttribute,
    computed,
    forwardRef,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';

/**
 *
 * @example
 * ```html
 * <adc-checkbox
 *    formControlName="formControlName"
 *    shape="rectangle"
 *    name="name"
 *    tabIndex="1"
 *    disabled="true"
 *    ariaLabel="ariaLabel"
 *    initialFocus="true"
 *    label="Text in first row"
 *    subtext="Text in second row"
 *    indeterminate="true"
 *    labelPosition="before"
 * />
 * ```
 */
@Component({
    selector: 'adc-checkbox',
    templateUrl: './adc-checkbox.component.html',
    styleUrl: './adc-checkbox.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatCheckbox],
    host: {
        '[attr.tabindex]': 'null',
        '[class.adc-checkbox--big]': 'label() && subtext()',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AdcCheckboxComponent),
            multi: true,
        },
    ],
})
export class AdcCheckboxComponent implements ControlValueAccessor, AfterViewInit {
    private readonly labelTextRef = viewChild<ElementRef<HTMLElement>>('labelTextRef');
    private readonly subtextRef = viewChild<ElementRef<HTMLElement>>('subtextRef');
    private readonly inputRef = viewChild.required<MatCheckbox>('inputRef');

    readonly disabled = input(false, { transform: booleanAttribute });
    readonly name = input<string | undefined>(undefined);
    readonly value = input<string | undefined>(undefined);
    readonly initialFocus = input(false, { transform: booleanAttribute });
    readonly tabIndex = input<number | undefined>(undefined);
    readonly ariaLabel = input<string>('');
    readonly shape = input<'rectangle' | 'round'>('rectangle');
    readonly label = input<string>('');
    readonly subtext = input<string>('');
    readonly indeterminate = input(false, { transform: booleanAttribute });
    readonly labelPosition = input<'before' | 'after'>('after');

    readonly focused = output<void>();
    readonly blurred = output<void>();
    readonly changed = output<boolean>();

    checked = signal(false);

    private readonly checkboxDisabled = signal(false);
    isDisabled = computed(() => this.disabled() || this.checkboxDisabled());

    /** Label and subtext elements, for `[adcTooltipTruncationTarget]` on the checkbox host. */
    readonly truncationTargets = computed(() =>
        [this.labelTextRef()?.nativeElement, this.subtextRef()?.nativeElement].filter(
            (element): element is HTMLElement => !!element,
        ),
    );

    private onChangeCallback: (value: boolean) => void = () => {};
    private onTouchedCallback: () => void = () => {};

    ngAfterViewInit(): void {
        if (this.initialFocus()) {
            queueMicrotask(() => this.focus());
        }
    }

    writeValue(value: boolean | null): void {
        this.checked.set(!!value);
    }

    registerOnChange(fn: (value: boolean) => void): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.checkboxDisabled.set(isDisabled);
    }

    focus(): void {
        this.inputRef().focus();
    }

    onChange(event: MatCheckboxChange): void {
        if (this.isDisabled()) {
            return;
        }

        const nextValue = event.checked;

        this.checked.set(nextValue);
        this.onChangeCallback(nextValue);
        this.changed.emit(nextValue);
    }

    onFocus(): void {
        this.focused.emit();
    }

    onBlur(): void {
        this.onTouchedCallback();
        this.blurred.emit();
    }
}
